// ─── EasyMoney Dual Theme System ─────────────────────────────────────────────
// Two themes: 'light' = violet/indigo gradient, 'dark' = neon-tech dark.
// `C` is a live object mutated by applyTheme(mode); all `C.x` reads stay valid.

const THEMES = {
  light: {
    mode: 'light',
    grad: 'linear-gradient(135deg, #7C5CFC 0%, #5B7CFA 100%)',
    gradSoft: 'linear-gradient(135deg, rgba(124,92,252,0.12) 0%, rgba(91,124,250,0.12) 100%)',
    primary: '#6D5BF0',
    accent: '#5B7CFA',

    income: '#12B886', incomeSoft: 'rgba(18,184,134,0.10)',
    expense: '#FF6B5B', expenseSoft: 'rgba(255,107,91,0.10)',
    transfer: '#7C5CFC', transferSoft: 'rgba(124,92,252,0.10)',
    warn: '#E8A317', warnSoft: 'rgba(232,163,23,0.12)',

    bg: '#F4F3FB', bgDeep: '#ECEAF8',
    card: '#FFFFFF', cardAlt: '#FAFAFE',
    border: '#E8E6F4', borderStrong: '#D8D4EE',

    ink: '#1C1B2E', inkSoft: '#5B5870', inkFaint: '#9B98B3',

    shadowSm: '0 1px 3px rgba(28,27,46,0.06)',
    shadowMd: '0 4px 16px rgba(91,69,217,0.08), 0 2px 6px rgba(28,27,46,0.05)',
    shadowLg: '0 12px 40px rgba(91,69,217,0.14), 0 4px 12px rgba(28,27,46,0.06)',
    glow: '0 8px 28px rgba(124,92,252,0.35)',

    onGrad: '#FFFFFF', onGradSoft: 'rgba(255,255,255,0.75)', onGradFaint: 'rgba(255,255,255,0.65)',
    chartIncome: '#12B886', chartExpense: '#FF6B5B', chartGrid: '#ECECEC',
  },
  dark: {
    mode: 'dark',
    grad: 'linear-gradient(135deg, #1A1A2E 0%, #16213E 100%)',
    gradSoft: 'rgba(54,228,218,0.12)',
    primary: '#36E4DA',
    accent: '#5B7CFA',

    income: '#36E4DA', incomeSoft: 'rgba(54,228,218,0.12)',
    expense: '#FF6B8B', expenseSoft: 'rgba(255,107,139,0.12)',
    transfer: '#9B8CFF', transferSoft: 'rgba(155,140,255,0.14)',
    warn: '#F5C451', warnSoft: 'rgba(245,196,81,0.14)',

    bg: '#0C0C14', bgDeep: '#15151F',
    card: '#15151F', cardAlt: '#1B1B28',
    border: '#232336', borderStrong: '#34344A',

    ink: '#FFFFFF', inkSoft: '#A8A8C0', inkFaint: '#6E6E88',

    shadowSm: '0 1px 3px rgba(0,0,0,0.4)',
    shadowMd: '0 4px 16px rgba(0,0,0,0.4)',
    shadowLg: '0 12px 40px rgba(0,0,0,0.5)',
    glow: '0 0 24px rgba(54,228,218,0.35)',

    onGrad: '#FFFFFF', onGradSoft: 'rgba(255,255,255,0.70)', onGradFaint: 'rgba(255,255,255,0.55)',
    chartIncome: '#36E4DA', chartExpense: '#FF6B8B', chartGrid: '#232336',
  },
};

// Live color object — starts in light. Mutated by applyTheme.
export const C = { ...THEMES.light };

export function applyTheme(mode) {
  const next = THEMES[mode] || THEMES.light;
  Object.keys(C).forEach(k => { delete C[k]; });
  Object.assign(C, next);
  return C;
}

// Primary fill/text helpers (dark mode uses solid neon pill with dark text)
export function primaryFill() { return C.mode === 'dark' ? C.primary : C.grad; }
export function primaryText() { return C.mode === 'dark' ? '#0C0C14' : '#fff'; }

// Style helpers read from the live C at call time (getters).
export const S = {
  get input() {
    return {
      width: '100%', padding: '10px 13px', border: `1.5px solid ${C.border}`,
      borderRadius: 12, fontSize: 13.5, boxSizing: 'border-box', background: C.card,
      outline: 'none', transition: 'all 0.18s ease', color: C.ink, fontFamily: 'inherit',
    };
  },
  btn(primary) {
    return primary ? {
      padding: '9px 18px', background: primaryFill(), color: primaryText(),
      border: 'none', borderRadius: 12, cursor: 'pointer', fontSize: 13.5, fontWeight: 600,
      boxShadow: C.glow, transition: 'all 0.2s ease', fontFamily: 'inherit', letterSpacing: '0.2px',
    } : {
      padding: '9px 18px', background: C.card, color: C.inkSoft,
      border: `1.5px solid ${C.border}`, borderRadius: 12, cursor: 'pointer',
      fontSize: 13.5, fontWeight: 500, transition: 'all 0.2s ease', fontFamily: 'inherit',
    };
  },
};

export function ThemeStyles({ mode = 'light' }) {
  const t = THEMES[mode] || THEMES.light;
  const isDark = mode === 'dark';
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@400;500;600;700&family=Space+Grotesk:wght@400;500;600;700&display=swap');

      * { font-family: ${isDark ? "'Space Grotesk'" : "'Plus Jakarta Sans'"}, -apple-system, sans-serif; }
      h1,h2,h3,.display { font-family: ${isDark ? "'Space Grotesk'" : "'Sora'"}, sans-serif; }

      body { margin: 0; background: ${t.bg}; transition: background 0.3s ease; }

      .em-mesh {
        background:
          ${isDark
            ? `radial-gradient(circle at 15% 20%, rgba(54,228,218,0.10), transparent 45%),
               radial-gradient(circle at 85% 15%, rgba(91,124,250,0.10), transparent 45%),
               radial-gradient(circle at 50% 90%, rgba(54,228,218,0.06), transparent 50%),
               ${t.bg}`
            : `radial-gradient(circle at 15% 20%, rgba(124,92,252,0.18), transparent 45%),
               radial-gradient(circle at 85% 15%, rgba(91,124,250,0.16), transparent 45%),
               radial-gradient(circle at 50% 90%, rgba(124,92,252,0.12), transparent 50%),
               ${t.bg}`};
        background-size: 200% 200%;
        animation: em-drift 18s ease-in-out infinite;
      }
      @keyframes em-drift {
        0%,100% { background-position: 0% 0%, 100% 0%, 50% 100%, 0 0; }
        50% { background-position: 30% 20%, 70% 30%, 40% 80%, 0 0; }
      }

      @keyframes em-fade-up { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes em-pop { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
      .em-fade-up { animation: em-fade-up 0.5s ease both; }
      .em-pop { animation: em-pop 0.35s ease both; }

      input:focus, select:focus {
        border-color: ${t.primary} !important;
        box-shadow: 0 0 0 3.5px ${isDark ? 'rgba(54,228,218,0.18)' : 'rgba(124,92,252,0.14)'} !important;
      }

      .em-btn-primary:hover { transform: translateY(-1.5px); box-shadow: ${isDark ? '0 0 28px rgba(54,228,218,0.55)' : '0 12px 32px rgba(124,92,252,0.45)'} !important; }
      .em-btn-primary:active { transform: translateY(0); }
      .em-btn-ghost:hover { border-color: ${t.borderStrong} !important; background: ${t.cardAlt} !important; color: ${t.ink} !important; }

      .em-card-hover { transition: transform 0.2s ease, box-shadow 0.2s ease; }
      .em-card-hover:hover { transform: translateY(-2px); box-shadow: ${t.shadowLg}; }

      .em-nav:hover { background: ${t.gradSoft} !important; }
      .em-nav-active:hover { filter: brightness(1.08); }
      .em-acct:hover { background: ${t.cardAlt} !important; }

      .em-row { transition: background 0.15s ease; }
      .em-row:hover { background: ${t.cardAlt} !important; }

      ::-webkit-scrollbar { width: 9px; height: 9px; }
      ::-webkit-scrollbar-thumb { background: ${t.borderStrong}; border-radius: 8px; }
      ::-webkit-scrollbar-thumb:hover { background: ${t.inkFaint}; }
      ::-webkit-scrollbar-track { background: transparent; }

      .em-grad-text {
        background: ${isDark ? 'linear-gradient(135deg,#36E4DA,#5B7CFA)' : t.grad};
        -webkit-background-clip: text; background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      @media (max-width: 640px) {
        .em-grid-stats { grid-template-columns: 1fr !important; }
      }
    `}</style>
  );
}
